import React from 'react';
import { STAGES, STAGE_CONFIG } from '../../../models/LiveMeetingsStore';
import {
  ExpandedPanel,
  PanelGrid,
  PanelField,
  PanelLabel,
  PanelValue,
  PanelLink,
  PanelSection,
  PanelSectionTitle,
  ParticipantList,
  ParticipantChip,
  Timeline,
  TimelineItem,
  TimelineDot,
  TimelineContent,
  TimelineLabel,
  TimelineTime,
} from '../LiveMeetingsDashboard.styles';

function formatDateTime(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// Map from stage name to the field in meetingData that holds the timestamp
const STAGE_TIMESTAMP_FIELDS = {
  scheduled:        'scheduledStartTime',
  recording:        'recordingStartedAt',
  queued:           'queuedAt',
  transcribing:     'transcriptionStartedAt',
  transcribed:      'transcriptionCompletedAt',
  identified:       'identifiedAt',
  needs_triage:     'triageCreatedAt',
  assigned:         'assignedAt',
  analyzing:        'analysisStartedAt',
  delivered:        'deliveredAt',
  email_sent:       'emailSentAt',
  failed:           'failedAt',
};

// Stages to show in the processing timeline (excludes approval_pending)
const PIPELINE_STAGES = STAGES.filter(s => s !== 'approval_pending');

function getTimelineStatus(stage, currentStage) {
  const currentIdx = PIPELINE_STAGES.indexOf(currentStage);
  const stageIdx   = PIPELINE_STAGES.indexOf(stage);
  if (currentStage === 'failed') {
    // All stages up to and including the failed stage are "done" conceptually,
    // but we mark the current as failed.
    // We can't know exactly which stage failed from this data, so mark current as failed.
    if (stageIdx < currentIdx) return 'done';
    if (stageIdx === currentIdx) return 'failed';
    return 'pending';
  }
  if (stageIdx < currentIdx)  return 'done';
  if (stageIdx === currentIdx) {
    // email_sent is the final terminal step — nothing advances past it, so
    // render it as 'done' (green + ✓) the moment it's reached, matching how
    // we'd visually treat any "completed" pipeline.
    if (stage === 'email_sent') return 'done';
    return 'current';
  }
  return 'pending';
}

export default function ProcessingPanel({ item }) {
  const { meetingData, triageData, stage, participantEmails, meetingUrl, meetingTitle, scheduledStartTime } = item;

  const recallBotId = meetingData?.recallBotId || meetingData?.id || '—';
  const assignedTo  = meetingData?.assignedTo || meetingData?.metadata?.assignedTo;

  // Merge timestamps: prefer meetingData fields, fall back to triageData
  const timestamps = {
    ...(triageData  || {}),
    ...(meetingData || {}),
  };

  return (
    <ExpandedPanel>
      {/* Meeting summary */}
      <PanelGrid $cols={3}>
        <PanelField>
          <PanelLabel>Meeting</PanelLabel>
          <PanelValue>{meetingTitle || '(No title)'}</PanelValue>
        </PanelField>

        {meetingUrl && (
          <PanelField>
            <PanelLabel>Meeting URL</PanelLabel>
            <PanelLink href={meetingUrl} target="_blank" rel="noopener noreferrer">
              {meetingUrl}
            </PanelLink>
          </PanelField>
        )}

        <PanelField>
          <PanelLabel>Scheduled Start</PanelLabel>
          <PanelValue>{formatDateTime(scheduledStartTime) || '—'}</PanelValue>
        </PanelField>

        <PanelField>
          <PanelLabel>Recall Bot ID</PanelLabel>
          <PanelValue style={{ fontFamily: 'monospace', fontSize: 12 }}>{recallBotId}</PanelValue>
        </PanelField>

        {assignedTo && (
          <PanelField>
            <PanelLabel>Assigned To</PanelLabel>
            <PanelValue>{assignedTo}</PanelValue>
          </PanelField>
        )}
      </PanelGrid>

      {/* Participants */}
      {participantEmails && participantEmails.length > 0 && (
        <PanelSection>
          <PanelSectionTitle>Participants ({participantEmails.length})</PanelSectionTitle>
          <ParticipantList>
            {participantEmails.map(email => (
              <ParticipantChip key={email}>{email}</ParticipantChip>
            ))}
          </ParticipantList>
        </PanelSection>
      )}

      {/* Processing timeline */}
      <PanelSection>
        <PanelSectionTitle>Processing Timeline</PanelSectionTitle>
        <Timeline>
          {PIPELINE_STAGES.map((s, i) => {
            const status    = getTimelineStatus(s, stage);
            const cfg       = STAGE_CONFIG[s];
            const tsField   = STAGE_TIMESTAMP_FIELDS[s];
            const timestamp = tsField ? timestamps[tsField] : null;
            const dim       = status === 'pending';

            return (
              <TimelineItem key={s} $done={status === 'done'}>
                <TimelineDot $status={status}>
                  {status === 'done' ? '✓' : status === 'failed' ? '✗' : cfg.icon}
                </TimelineDot>
                <TimelineContent>
                  <TimelineLabel $dim={dim}>
                    {cfg.label}
                  </TimelineLabel>
                  {timestamp && (
                    <TimelineTime>{formatDateTime(timestamp)}</TimelineTime>
                  )}
                  {!timestamp && status === 'current' && (
                    <TimelineTime>In progress…</TimelineTime>
                  )}
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </PanelSection>
    </ExpandedPanel>
  );
}
